import { FormEvent, useEffect, useState } from "react"
import GameScreenLayout from "@/components/GameScreenLayout"
import { useAuth } from "@/context/AuthContext"
import { resolveProfileCityId } from "@/features/passport/guardiansCity"
import {
  connectCityChatSocket,
  disconnectCityChatSocket,
  emitCityChatEvent,
  onCityChatEvent,
} from "@/socket/cityChatSocket"
import { CityChatMessage, CityChatState } from "@/types/cityChat"
import {
  markCityChatRead,
  noteCityChatHistory,
  noteCityChatIncoming,
  setCityChatViewing,
} from "@/features/city/cityChatUnread"

export default function CityChatPage() {
  const { profile } = useAuth()
  const cityId = resolveProfileCityId(profile)
  const [state, setState] = useState<CityChatState | null>(null)
  const [messages, setMessages] = useState<CityChatMessage[]>([])
  const [text, setText] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [connected, setConnected] = useState(false)

  const selfUserId =
    typeof profile?.user_id === "number"
      ? profile.user_id
      : typeof profile?.id === "number"
        ? profile.id
        : null

  useEffect(() => {
    void setCityChatViewing(true)
    return () => {
      void setCityChatViewing(false)
    }
  }, [])

  useEffect(() => {
    if (cityId == null) return

    let cancelled = false

    void connectCityChatSocket()
      .then(() => {
        if (cancelled) return
        setConnected(true)
        emitCityChatEvent("chat-join", { cityId })
        emitCityChatEvent("chat-refresh", { cityId })
      })
      .catch(() => {
        if (!cancelled) setError("Не удалось подключиться к чату")
      })

    const offState = onCityChatEvent("chat-state", (payload) => {
      setState(payload)
      const msgs = payload.messages ?? []
      setMessages(msgs)
      void noteCityChatHistory({
        messages: msgs.map((m) => ({
          createdAt: m.createdAt,
          author: { userId: m.author?.userId },
        })),
        selfUserId,
      })
    })

    const offMessage = onCityChatEvent("chat-message", (payload) => {
      void noteCityChatIncoming({
        createdAt: payload.createdAt,
        authorUserId: payload.author?.userId,
        selfUserId,
      })
      setMessages((prev) => {
        if (prev.some((m) => m.id === payload.id)) return prev
        return [...prev, payload]
      })
    })

    const offDeleted = onCityChatEvent("chat-message-deleted", (payload) => {
      setMessages((prev) => prev.filter((m) => m.id !== payload.messageId))
    })

    const offCleared = onCityChatEvent("chat-cleared", () => {
      setMessages([])
    })

    return () => {
      cancelled = true
      offState()
      offMessage()
      offDeleted()
      offCleared()
      disconnectCityChatSocket()
    }
  }, [cityId, selfUserId])

  useEffect(() => {
    if (messages.length === 0) return
    const maxMs = messages.reduce((acc, m) => {
      const t = Date.parse(m.createdAt ?? "")
      return Number.isFinite(t) ? Math.max(acc, t) : acc
    }, 0)
    if (maxMs > 0) void markCityChatRead(maxMs)
  }, [messages])

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    const trimmed = text.trim()
    if (!trimmed || !connected) return
    if (state?.ban) {
      setError("Вы заблокированы в чате")
      return
    }
    if (!emitCityChatEvent("chat-send", { text: trimmed })) {
      setError("Нет соединения с чатом")
      return
    }
    setText("")
    setError(null)
  }

  if (cityId == null) {
    return (
      <GameScreenLayout title="Чат города" backTo="/play">
        <p className="text-sm text-guardians-muted">
          Сначала выберите город в профиле или пройдите онбординг.
        </p>
      </GameScreenLayout>
    )
  }

  const maxLen = state?.maxLength ?? 500
  const title = state?.cityTitle ? `Чат: ${state.cityTitle}` : "Чат города"

  return (
    <GameScreenLayout title={title} backTo="/play">
      {state?.ban ? (
        <p className="mb-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
          Блокировка чата до{" "}
          {state.ban.permanent
            ? "бессрочно"
            : state.ban.expiresAt ?? "окончания срока"}
        </p>
      ) : null}

      <div className="flex max-h-[calc(100dvh-14rem)] flex-col gap-3">
        <ul className="min-h-0 flex-1 space-y-2 overflow-y-auto rounded-xl border border-white/10 bg-black/20 p-3">
          {messages.length === 0 ? (
            <li className="text-sm text-guardians-muted">Сообщений пока нет</li>
          ) : (
            messages.map((msg) => (
              <li key={msg.id} className="text-sm">
                <span className="font-medium text-guardians-accent">
                  {msg.author.nickname}
                </span>
                <span className="text-guardians-muted">
                  {" "}
                  · {new Date(msg.createdAt).toLocaleTimeString("ru-RU", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
                <p className="mt-0.5 whitespace-pre-wrap break-words">{msg.text}</p>
              </li>
            ))
          )}
        </ul>

        <form onSubmit={onSubmit} className="flex gap-2">
          <input
            type="text"
            value={text}
            maxLength={maxLen}
            disabled={!connected || Boolean(state?.ban) || state?.enabled === false}
            onChange={(e) => setText(e.target.value)}
            placeholder={
              state?.enabled === false ? "Чат отключён" : "Сообщение…"
            }
            className="min-w-0 flex-1 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm"
          />
          <button
            type="submit"
            disabled={!text.trim() || !connected}
            className="rounded-xl bg-guardians-accent px-4 py-2 text-sm font-medium text-guardians-bg disabled:opacity-40"
          >
            Отправить
          </button>
        </form>
        {error ? <p className="text-xs text-red-300">{error}</p> : null}
      </div>
    </GameScreenLayout>
  )
}
